/**
 * Shared authentication types — canonical TS contracts for the user, the
 * OAuth token payload, and the API response envelope.
 *
 * Mirrors the backend contracts defined in
 * - `App\Enums\UserRole` (role enum)
 * - `App\Services\Domain\Authentication\DTO\AuthenticationResult` (token + user payload)
 * - `App\Http\API\V1\Response\ApiResponse` (envelope shape)
 *
 * SH-AUTH-005.
 */

/**
 * Canonical role strings accepted by the platform. These are the literal
 * values of the server-side `UserRole` enum — keep in sync.
 */
export type UserRole =
  | 'superuser'
  | 'admin'
  | 'manager'
  | 'csr'
  | 'prepress'
  | 'fulfillment'
  | 'customer'
  // Legacy / documented-but-not-yet-exposed role strings from the original
  // spec draft. Accepted here so older tokens / test fixtures do not break
  // downstream code while the taxonomy stabilises.
  | 'super_admin'
  | 'design_team'
  | 'production';

/**
 * Permission strings are free-form and resolved server-side. We only need
 * to know they are strings — per-permission narrowing happens where the
 * resolver has context.
 */
export type Permission = string;

/**
 * Shape of the user profile object returned by `/auth/login` and `/auth/me`.
 * Field names are camelCase because the server templates emit camelCase at
 * the API boundary.
 */
export interface AuthUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  roles: UserRole[];
  permissions: Permission[];
  /**
   * Optional profile fields hydrated from `/auth/login` and `/auth/me`.
   * The dashboard's account-completion checklist (SF-ACCT-001) reads
   * these — null/undefined means "not yet provided" and the relevant
   * checkpoint renders as incomplete.
   */
  companyName?: string | null;
  phone?: string | null;
  /** ISO-8601 datetime when the user verified their email; null when unverified. */
  emailVerifiedAt?: string | null;
  /**
   * Whether the user has confirmed TOTP-based MFA enrollment. Reflected
   * in the in-memory session by the customer Security page (SF-ACCT-009)
   * when MFA is enabled or disabled, so subsequent flows that gate on
   * MFA enrollment status (SF-AUTH-011 challenge) read a fresh value
   * without re-fetching `/auth/me`.
   */
  mfaEnabled?: boolean;
}

/**
 * The `authorization` block emitted by the server envelope on login /
 * refresh / heartbeat. All fields are snake_case because the platform
 * emits them that way (see
 * {@link AuthenticationResult::authorizationArray()}).
 */
export interface AuthorizationPayload {
  token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
  refresh_token_expires_in?: number;
  scopes?: string[];
}

/**
 * The server envelope shape. Every successful JSON response from the
 * platform follows this skeleton; only `data` / `authorization` / `meta`
 * are populated on success, and error paths swap `status` → `'error'`
 * plus attach `error` / `errors` / `error_code` / `severity`.
 */
export interface ApiEnvelope<TData = Record<string, unknown>> {
  status: 'success' | 'error';
  message: string;
  authorization?: AuthorizationPayload;
  data?: TData;
  meta: {
    alerts?: unknown[];
    pagination?: unknown;
    [key: string]: unknown;
  };
  errors?: Record<string, string[]>;
  error_code?: string;
  error?: {
    code: string;
    message: string;
    context?: Record<string, unknown>;
  };
  resources?: Record<string, unknown>;
  severity?: 'info' | 'warning' | 'error' | 'critical';
}

/**
 * Shape of `data` for a successful login (non-MFA branch). The
 * `authorization` block rides alongside on the envelope, not inside `data`.
 */
export interface LoginData {
  user: AuthUser;
}

/**
 * Alternative `data` shape for the MFA-required branch of `/auth/login`.
 */
export interface MfaChallengeData {
  mfa_required: true;
  challenge_token: string;
}

/**
 * Success return value from the typed `auth.login` helper. Discriminates
 * between a completed login and an MFA challenge.
 */
export type LoginResult =
  | {
      mfaRequired: false;
      user: AuthUser;
      authorization: AuthorizationPayload;
    }
  | {
      mfaRequired: true;
      challengeToken: string;
    };

/**
 * Return shape from `auth.refresh` / `auth.heartbeat` — just the tokens.
 */
export interface TokenRefreshResult {
  authorization: AuthorizationPayload;
}

/**
 * OAuth client identifier — which client record the backend uses to
 * issue tokens. Storefront and Console SPAs always pass their own
 * identifier so the server applies the right TTL profile (120s access /
 * 420s refresh for SPAs; 3600s / 30d for external API callers).
 */
export type AuthClient = 'console' | 'storefront' | 'external';

/**
 * The first-party SPAs. Each owns its own refresh cookie on the platform, so
 * a bundle must say which one it is on refresh and logout.
 */
export type SpaAuthClient = Exclude<AuthClient, 'external'>;

/**
 * Login request body.
 *
 * `client` is translated to `client_type` on the wire by the shared
 * `authApi.login` helper — callers use the terser TS name.
 */
export interface LoginRequest {
  email: string;
  password: string;
  client?: AuthClient;
}

/**
 * Business-type enum accepted by `POST /auth/register`. The server stores
 * the snake_case value verbatim; the UI renders a human-readable label.
 */
export type BusinessType = 'installer' | 'sign_shop' | 'fleet_manager' | 'other';

/**
 * US-only address sub-object on the register request body. Composed inside
 * the Zod schema as discrete sub-fields, then assembled into this block
 * before posting to the server.
 */
export interface RegisterAddress {
  line1: string;
  city: string;
  state: string;
  zip: string;
}

/**
 * Request body for `POST /auth/register`. Mirrors the snake_case contract
 * the platform expects; the client-side `confirmPassword` is NEVER sent.
 */
export interface RegisterRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  company_name: string;
  business_type: BusinessType;
  phone: string;
  address: RegisterAddress;
}

/**
 * Success return shape from the typed `auth.register` helper. Mirrors the
 * non-MFA login success branch — newly registered accounts never require
 * MFA on the first session.
 */
export interface RegisterResult {
  user: AuthUser;
  authorization: AuthorizationPayload;
}

/**
 * `/auth/register` answered 202: the email belongs to a deleted customer, so
 * no account was created and a restore link was emailed instead (#1828).
 */
export interface RegisterRestorePending {
  restorePending: true;
}

export type RegisterOutcome = RegisterResult | RegisterRestorePending;

export function isRestorePending(outcome: RegisterOutcome): outcome is RegisterRestorePending {
  return 'restorePending' in outcome;
}
